import { formatHoursToHM } from '@/composable/useFormatDateAndHours'
import { useReportStore } from '@/store/report'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

export function useUtilizationGoalsPercentChart(sensorData: any[], sensorCompareData: any[]) {
  const { t } = useI18n()
  const reportStore = useReportStore()

  const categories = ref(sensorData?.map((sensor) => sensor.sensorName) || [])

  const seriesPercent = ref([
    {
      name: 'Período Base',
      data: sensorData?.map((sensor) => sensor.totalUtilizationPercentage) || [],
    },
    {
      name: 'Meta',
      data: sensorData?.map((sensor) => sensor.metaPercentage) || [],
    },
    {
      name: 'Período Comparação',
      data: sensorCompareData?.map((sensor) => sensor.totalUtilizationPercentage) || [],
    },
  ])

  const chartOptionsPercent = ref({
    series: seriesPercent.value,
    chart: {
      type: 'bar',
      toolbar: {
        show: false,
        tools: { download: false },
      },
    },
    colors: [],
    plotOptions: {
      bar: {
        horizontal: false,
        dataLabels: { position: 'top' },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val?.toFixed(0)} %`,
      style: {
        fontSize: '14px',
        fontWeight: 'bold',
        colors: ['#fff'],
      },
      background: {
        enabled: true,
        foreColor: '#000',
        padding: 6,
        borderColor: '#000',
        borderWidth: 1,
        opacity: 1,
      },
    },
    stroke: {
      show: true,
      width: 1,
      colors: ['#fff'],
    },
    legend: {
      show: true,
      fontSize: '14px',
      fontWeight: 'regular',
      labels: {
        colors: ['#000'],
        useSeriesColors: true,
      },
      position: 'bottom',
      offsetY: 10,
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val: number) => `${val?.toFixed(0)} %`,
      },
    },
    yaxis: {
      labels: {
        formatter: (val: number) => `${val?.toFixed(0)} %`,
        style: {
          fontSize: '14px',
          fontWeight: 'regular',
          colors: ['#000'],
        },
      },
    },
    xaxis: {
      categories: categories.value,
      labels: {
        style: {
          fontSize: '14px',
          fontWeight: 'regular',
          colors: ['#000'],
        },
      },
    },
  })

  watch(
    () => [reportStore.getBaseChartColors, reportStore.getCompareChartColors],
    ([baseColor, compareColor]) => {
      chartOptionsPercent.value = {
        ...chartOptionsPercent.value,
        colors: [baseColor, '#353a45', compareColor],
      }
    },
    { immediate: true, deep: true },
  )

  watch(
    () => reportStore.getEnergySensorUtilization,
    (newSensorConsumption) => {
      if (newSensorConsumption?.length) {
        seriesPercent.value[0].data = newSensorConsumption.map((sensor) => sensor.totalUtilization)

        seriesPercent.value[1].data = newSensorConsumption.map((sensor) => sensor.metaPercentage)
        categories.value = newSensorConsumption.map((sensor) => sensor.sensorName)
        chartOptionsPercent.value = {
          ...chartOptionsPercent.value,
          xaxis: {
            ...chartOptionsPercent.value.xaxis,
            categories: categories.value,
          },
        }
      }
    },
    { immediate: true, deep: true },
  )

  watch(
    () => reportStore.getCompareEnergySensorUtilization,
    (newSensorConsumption) => {
      if (newSensorConsumption?.length) {
        seriesPercent.value[2].data = newSensorConsumption.map((sensor) => sensor.totalUtilization)
        if (categories.value.length === 0) {
          categories.value = newSensorConsumption.map((sensor) => sensor.sensorName)
          chartOptionsPercent.value = {
            ...chartOptionsPercent.value,
            xaxis: {
              ...chartOptionsPercent.value.xaxis,
              categories: categories.value,
            },
          }
        }
      } else {
        seriesPercent.value[2].data = []
      }
    },
    { immediate: true, deep: true },
  )

  const seriesHours = ref([
    {
      name: 'Período Base',
      data: sensorData?.map((sensor) => sensor.totalUtilizationHours) || [],
    },
    {
      name: 'Meta',
      data: sensorData?.map((sensor) => sensor.metaHours) || [],
    },
    {
      name: 'Período Comparação',
      data: sensorCompareData?.map((sensor) => sensor.totalUtilizationHours) || [],
    },
  ])

  const chartOptionsHour = ref({
    series: seriesHours.value,
    chart: {
      type: 'bar',
      toolbar: {
        show: false,
        tools: { download: false },
      },
    },
    colors: [],
    plotOptions: {
      bar: {
        horizontal: false,
        dataLabels: { position: 'top' },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => formatHoursToHM(val),
      style: {
        fontSize: '14px',
        fontWeight: 'bold',
        colors: ['#fff'],
      },
      background: {
        enabled: true,
        foreColor: '#000',
        padding: 6,
        borderColor: '#000',
        borderWidth: 1,
        opacity: 1,
      },
    },
    legend: {
      show: true,
      fontSize: '14px',
      fontWeight: 'regular',
      labels: {
        colors: ['#000'],
        useSeriesColors: true,
      },
      position: 'bottom',
      offsetY: 10,
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val: number) => formatHoursToHM(val),
      },
    },
    yaxis: {
      labels: {
        formatter: (val: number) => formatHoursToHM(val),
        style: {
          fontSize: '14px',
          fontWeight: 'regular',
          colors: ['#000'],
        },
      },
    },
    xaxis: {
      categories: categories.value,
      labels: {
        style: {
          fontSize: '14px',
          fontWeight: 'regular',
          colors: ['#000'],
        },
      },
    },
  })

  watch(
    () => [reportStore.getBaseChartColors, reportStore.getCompareChartColors],
    ([baseColor, compareColor]) => {
      chartOptionsHour.value = {
        ...chartOptionsHour.value,
        colors: [baseColor, '#353a45', compareColor],
      }
    },
    { immediate: true, deep: true },
  )

  watch(
    () => reportStore.getEnergySensorUtilization,
    (newSensorConsumption) => {
      if (newSensorConsumption?.length) {
        seriesHours.value[0].data = newSensorConsumption.map((sensor) => sensor.totalUtilizationHours)
        seriesHours.value[1].data = sensorData?.map((sensor) => sensor.metaHours) || []
        categories.value = newSensorConsumption.map((sensor) => sensor.sensorName)
        chartOptionsHour.value = {
          ...chartOptionsHour.value,
          xaxis: {
            ...chartOptionsHour.value.xaxis,
            categories: categories.value,
          },
        }
      }
    },
    { immediate: true, deep: true },
  )

  watch(
    () => reportStore.getCompareEnergySensorUtilization,
    (newSensorConsumption) => {
      if (newSensorConsumption?.length) {
        seriesHours.value[2].data = newSensorConsumption.map((sensor) => sensor.totalUtilizationHours)
        if (categories.value.length === 0) {
          categories.value = newSensorConsumption.map((sensor) => sensor.sensorName)
          chartOptionsHour.value = {
            ...chartOptionsHour.value,
            xaxis: {
              ...chartOptionsHour.value.xaxis,
              categories: categories.value,
            },
          }
        }
      } else {
        seriesHours.value[2].data = []
      }
    },
    { immediate: true, deep: true },
  )

  return { chartOptionsHour, chartOptionsPercent, seriesPercent, seriesHours }
}
